import React, { useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Hydrate, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import type { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import { initializeApp2WebInterface } from '@kcd/app-interface';
import { initializeTrackerInterface } from '@kcd/event-tracker';

import CommonStorageSyncWrapper from '~/components/CommonStorageSyncWrapper';
import ScrollButtons from '~/components/SearchButtons/ScrollButtons';
import useHistoryIndexChangeByRoute from '~/hooks/history';
import { usePosOpenedByReferrer } from '~/hooks/useReferrer';
import { createReactQueryClient } from '~/libs/reactQuery';
import type { NextPageWithLayout } from '~/types';
import { datadogInitialize } from '~/utils/datadog';
import { ENV } from '~/utils/env';
import { isClient } from '~/utils/ssr';

import AppHead from './AppHead';
import AppHooks from './AppHooks';
import { ProviderWrap } from './ProviderWrap';
import RootErrorFallback from './RootErrorFallback';

import '~/styles/global.css';
import '@kcd/design-system/dist/feather.css';
import '@kcd/design-system/ssr/index.css';

const DynamicProductOptionBottomsheet = dynamic(
  () => import('~/components/CommonProductBottomSheet'),
  {
    ssr: false,
  },
);

const DynamicProductCrossSellBottomsheet = dynamic(
  () => import('~/components/CommonProductCrossSellBottomSheet'),
  {
    ssr: false,
  },
);

const ScrollButtonsWrapper = () => {
  const isPosOpened = usePosOpenedByReferrer();

  return <ScrollButtons enabled={!!isPosOpened} />;
};

const DefaultLayout = ({ children }: { children: React.ReactNode }) => {
  return <>{children}</>;
};

type AppPropsWithLayout = Omit<AppProps, 'pageProps'> & {
  Component: NextPageWithLayout;
  pageProps?: {
    dehydratedState?: Record<string, unknown>;
    token?: string;
  };
};

const MyApp = ({ Component, pageProps }: AppPropsWithLayout) => {
  const [queryClient] = React.useState(() => createReactQueryClient());
  const Layout = Component.Layout ?? DefaultLayout;
  useHistoryIndexChangeByRoute();

  useEffect(() => {
    if (isClient()) {
      initializeApp2WebInterface();

      // local과 배포된것을 비교
      if (process.env.NODE_ENV === 'production') {
        initializeTrackerInterface({
          greenfinchConfig: {
            serviceName: 'kcd_cashnote',
            writeKey: (ENV === 'prod'
              ? process.env.NEXT_PUBLIC_GREENFINCH_WRITE_KEY
              : process.env.NEXT_PUBLIC_GREENFINCH_STAGING_WRITE_KEY) as string,
            debug: ENV !== 'prod',
          },
        });
      }
      if (ENV === 'prod') {
        datadogInitialize();
      }
    }
  }, []);

  useEffect(() => {
    if (window.localStorage.getItem('token')) return;

    if (pageProps?.token) {
      window.localStorage.setItem('token', pageProps.token);
    }
  }, [pageProps, queryClient]);

  return (
    <ErrorBoundary FallbackComponent={RootErrorFallback}>
      <QueryClientProvider client={queryClient}>
        <Hydrate state={pageProps?.dehydratedState}>
          <ProviderWrap>
            <AppHead />
            <AppHooks />
            <Layout>
              <ScrollButtonsWrapper />
              <Component {...pageProps} />
              <DynamicProductOptionBottomsheet />
              <DynamicProductCrossSellBottomsheet />
              <CommonStorageSyncWrapper />
            </Layout>
          </ProviderWrap>
        </Hydrate>
        <ReactQueryDevtools />
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default MyApp;
